/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES, PARENT_HUB_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { isProviderUser, isParentUser } from '../../../../util/onboarding';
import { isAdmin } from '../../../../util/mfa';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);
  // KidHive: parents book classes, providers run them. Providers create classes from the hub, so
  // the drawer stays minimal for them (Dashboard / Account / Log out); parents get Find classes.
  const isProvider = isProviderUser(user);
  const isParent = isParentUser(user);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const findClassesLinkMaybe =
    isAuthenticated && !isProvider ? (
      <NamedLink className={css.createNewListingLink} name="SearchPage">
        <FormattedMessage id="TopbarMobileMenu.findClassesLink" />
      </NamedLink>
    ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <ul className={css.customLinksWrapper}>{extraLinks}</ul>

          <div className={css.spacer} />
        </div>
        {/* Browsing is public, so signed-out visitors get the same footer CTA parents see. */}
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="SearchPage">
            <FormattedMessage id="TopbarMobileMenu.findClassesLink" />
          </NamedLink>
        </div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isParentHub = page === 'ParentInfoPage' && PARENT_HUB_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isParentHub || isInboxPage
      ? css.currentPage
      : null;
  };

  // Providers live in the dashboard hub -- the drawer is just Dashboard + Account (+ Log out above).
  const dashboardLinkMaybe = isProvider ? (
    <>
      <li className={classNames(css.navigationLink, currentPageClass('ProviderDashboardPage'))}>
        <NamedLink name="ProviderDashboardPage">
          <FormattedMessage id="TopbarMobileMenu.dashboardLink" />
        </NamedLink>
      </li>
      <li className={classNames(css.navigationLink, currentPageClass('ProviderAccountBasePage'))}>
        <NamedLink name="ProviderAccountBasePage">
          <FormattedMessage id="TopbarMobileMenu.providerAccountLink" />
        </NamedLink>
      </li>
    </>
  ) : null;

  // Parents browse; the row mirrors the bottom nav's "Explore" (the footer CTA keeps "Find classes").
  const findClassesRowMaybe = !isProvider ? (
    <li className={classNames(css.navigationLink, currentPageClass('SearchPage'))}>
      <NamedLink name="SearchPage">
        <FormattedMessage id="TopbarMobileMenu.exploreLink" />
      </NamedLink>
    </li>
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        {/* Same order as the mobile bottom navigation (Home, Bookings, Explore, Messages, Profile),
            with Settings after -- consistent wayfinding between the two menus. */}
        <ul className={css.accountLinksWrapper}>
          {isParent ? (
            <li className={classNames(css.navigationLink, currentPageClass('LandingPage'))}>
              <NamedLink name="LandingPage">
                <FormattedMessage id="TopbarMobileMenu.homeLink" />
              </NamedLink>
            </li>
          ) : null}
          {isParent ? (
            <li className={classNames(css.navigationLink, currentPageClass('BookingsPage'))}>
              <NamedLink name="BookingsBasePage">
                <FormattedMessage id="TopbarMobileMenu.bookingsLink" />
              </NamedLink>
            </li>
          ) : null}
          {findClassesRowMaybe}
          {!isProvider ? (
            <li className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}>
              <NamedLink name="InboxPage" params={{ tab: inboxTab }}>
                <FormattedMessage id="TopbarMobileMenu.messagesLink" />
                {notificationCountBadge}
              </NamedLink>
            </li>
          ) : null}
          {dashboardLinkMaybe}
          {isParent ? (
            <>
              <li className={classNames(css.navigationLink, currentPageClass('ParentInfoPage'))}>
                <NamedLink name="ParentInfoPage">
                  <FormattedMessage id="TopbarMobileMenu.profileLink" />
                </NamedLink>
              </li>
              <li className={classNames(css.navigationLink, currentPageClass('AccountSecurityPage'))}>
                <NamedLink name="AccountSecurityPage">
                  <FormattedMessage id="TopbarMobileMenu.settingsLink" />
                </NamedLink>
              </li>
            </>
          ) : null}
          {/* About + Legal moved to the shared app footer (client ask: consistent + a leaner menu). */}
          {/* Admins (metadata.role) get the admin area as the last entry, whatever their userType. */}
          {isAdmin(user) ? (
            <li className={classNames(css.navigationLink, currentPageClass('AdminPage'))}>
              <NamedLink name="AdminPage">
                <FormattedMessage id="TopbarMobileMenu.adminLink" />
              </NamedLink>
            </li>
          ) : null}
        </ul>
        <ul className={css.customLinksWrapper}>{extraLinks}</ul>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{findClassesLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
